import { Injectable } from '@angular/core';
import { State } from '@cemex/cmx-client-layout';
import { Observable, of, Subject } from 'rxjs';
import { STATUS_POD_CODE } from '../constants/status-pod';
import { Action, Store } from '@ngrx/store';
import { getJobsiteSelected, getPodAddressName } from '@ordersnproducts/modules/order-builder/reducers';
import { catchError, map, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { FormattedAddress } from '../models/FormattedAddress.interface';
import {
  LoadPointOfDeliveryAddressNameComplete,
  LoadPointOfDeliveryAddressNameFail,
} from '@ordersnproducts/modules/order-builder/modules/location/actions/point-of-delivery-actions/point-of-delivery.actions';
import { AddressesService, IAddressResponse } from '@cemex-core/cmx-api';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Features } from '@ordersnproducts/features';

@Injectable()
export class AddressService {
  addressNames: { [addressId: number]: FormattedAddress } = null;
  private onDestroy$ = new Subject<boolean>();

  /**
   * Constructor for AddressService.
   * @param store The NGRX Store instance.
   * @param addressesService Service for accessing address data.
   * @param featureToggleService Service for checking feature toggles.
   */
  constructor(
    private store: Store<State>,
    private addressesService: AddressesService,
    private featureToggleService: FeatureToggleService,
  ) {
    this.getAddressNames();
  }

  public formatAddress(address: IAddressResponse): FormattedAddress {
    return {
      addressId: address.addressId,
      addressStringFull: address.formattedAddress,
      geoplace: address.geoPlace,
      // TODO: IAddressResponse doesnt have 'crossStreetName2' property in current version of core
      crossStreetNames: (address.crossStreetName1 || '') + (address['crossStreetName2'] || ''),
    };
  }

  /**
   * Subscribes to store for Point of Delivery address names.
   * Updates `addressNames` with values retrieved from the store.
   */
  private getAddressNames() {
    this.store
      .select(getPodAddressName)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((addressNames) => {
        this.addressNames = addressNames || null;
      });
  }

  /**
   * Fetches addresses by their IDs from `addressesService`.
   * @param addressIds Array of address IDs.
   * @returns Observable with the address data or an empty array if an error occurs.
   */
  private getAddress(addressIds: string[]): Observable<any> {
    return this.addressesService.getAddressByIds(addressIds).pipe(
      takeUntil(this.onDestroy$),
      catchError((error) => {
        console.error('Error fetching addresses:', error);
        return of([]);
      }),
    );
  }

  /**
   * Maps and formats address data to `FormattedAddress` objects.
   * @param addresses Array of address data from the API.
   * @returns Array of formatted addresses.
   */
  private mapAndFormatAddresses(addresses: IAddressResponse[]): FormattedAddress[] {
    return addresses.map((address) => this.formatAddress(address));
  }

  /**
   * Finds the address name for a given address ID.
   * @param addressId The ID of the address.
   * @returns The full address string if found, or an empty string otherwise.
   */
  findAddressNameByAddressId(addressId: number): string {
    let addressName: FormattedAddress;
    if (!!this.addressNames && Object.values(this.addressNames).length > 0) {
      addressName = this.addressNames[addressId];
    }
    return !!addressName ? addressName.addressStringFull : '';
  }

  /**
   * Loads formatted addresses based on the provided action payload.
   * @param actionPayload Data payload to filter and retrieve addresses.
   * @returns An observable action that either completes successfully with formatted addresses or fails with an error.
   */
  loadFormattedAddresses(actionPayload: any): Observable<Action> {
    return this.filterActivePodsAndMapAddressIds(actionPayload).pipe(
      withLatestFrom(this.store.select(getJobsiteSelected)),
      switchMap(([addressIds, jobsite]: [string[], any]) => {
        addressIds.push(jobsite.address.addressId + '');
        if (this.isEnabledPODDelete) {
          return this.getAddress(addressIds).pipe(
            map((response: any) => {
              const addressParse = this.mapAndFormatAddresses(response.addresses);
              return new LoadPointOfDeliveryAddressNameComplete(addressParse);
            }),
            catchError((error) => of(new LoadPointOfDeliveryAddressNameFail(error))),
          );
        } else {
          return of(new LoadPointOfDeliveryAddressNameComplete([]));
        }
      }),
    );
  }

  /**
   * Filters active Point of Delivery (POD) and maps their address IDs.
   * @param pods List of PODs to filter.
   * @returns Observable with an array of active address IDs as strings.
   */
  private filterActivePodsAndMapAddressIds(pods: any): Observable<string[]> {
    const podsActive = pods.filter(
      (pod) => pod.shipmentLocationStatus.shipmentLocationStatusCode !== STATUS_POD_CODE.INACTIVE,
    );
    const addressIds = podsActive.map((pod) => pod.address.addressId + '');
    return of(addressIds);
  }

  /**
   * Checks if the feature toggle for deleting PODs is enabled.
   * @returns True if enabled, false otherwise.
   */
  get isEnabledPODDelete(): boolean {
    return this.featureToggleService.isFeatureEnabled(Features.EnablePODDropdownFeature);
  }
}
